/**
 * WorkspaceDetailPage — Research Hub layout
 *
 * Layout (matches mockup):
 *
 *   ← All Workspaces                              [Settings]
 *   Houston County Council Coverage
 *   Tracking council votes…                       description
 *   [City of Hokah] [City of La Crescent] [2023-present] [Development] [Land Use]
 *
 *   [Ask a question ↗]  [Browse documents ↗]
 *
 *   ┌──────────────────────────┬──────────────────────────┐
 *   │  CORPUS COVERAGE         │  QUICK STATS             │
 *   │  City of Hokah / bar     │  key-value rows           │
 *   │  City of La Crescent     │                          │
 *   ├──────────────────────────┤                          │
 *   │  RECENT QUESTIONS        ├──────────────────────────┤
 *   │  …questions…             │  SUGGESTED QUESTIONS     │
 *   └──────────────────────────┴──────────────────────────┘
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  FileText,
  Settings,
  ChevronDown,
  ChevronUp,
  TextSearch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useUser } from '@/hooks/useUser';
import { useWorkspaceDetail } from '@/hooks/useWorkspaceDetail';
import { usePageTitle } from '@/hooks/usePageTitle';
import { WorkspaceCoverageCard } from '@/components/workspaces/WorkspaceCoverageCard';
import { WorkspaceRecentQuestions } from '@/components/workspaces/WorkspaceRecentQuestions';
import { WorkspaceSuggestedQuestions } from '@/components/workspaces/WorkspaceSuggestedQuestions';
import { WorkspaceQuickStats } from '@/components/workspaces/WorkspaceQuickStats';
import { WorkspaceSettingsPanel } from '@/components/workspaces/WorkspaceSettingsPanel';
import type { Workspace } from '@/types/database';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).getFullYear().toString();
}

/** Build the tag pills shown under the workspace name */
function buildTags(
  workspace: Workspace,
  jurisdictionNames: string[]
): string[] {
  const tags: string[] = [];

  // Jurisdiction pills
  for (const name of jurisdictionNames) {
    tags.push(name);
  }

  // Date range pill
  const fromYear = fmtYear(workspace.date_from);
  const toYear = fmtYear(workspace.date_to);
  if (fromYear && toYear) tags.push(`${fromYear} – ${toYear}`);
  else if (fromYear) tags.push(`${fromYear} – present`);

  // Kind pill when custom/themed (jurisdiction kind is implied by the city pills)
  // Only show for 'themed' — 'custom' is an internal workspace type, not useful to display
  if (workspace.kind === 'themed') {
    tags.push('Themed');
  }

  return tags;
}

// ─── WorkspaceDetailPage ──────────────────────────────────────────────────────

export const WorkspaceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { workspaces, loading: workspacesLoading } = useWorkspaces();
  const { user: currentUser } = useUser();

  const contextWorkspace = workspaces.find((w) => w.id === id) ?? null;
  const { data, loading, error, refetch } = useWorkspaceDetail(id);
  const apiWorkspace = data?.workspace ? (data.workspace as Workspace) : null;

  const [nameOverride, setNameOverride] = useState<string | null>(null);

  // The header/shell renders as soon as ANY source has the workspace row
  // (the workspace list arrives in <1s, long before /detail completes) —
  // individual cards keep their own loading skeletons below.
  const displayWorkspace: Workspace | null = (() => {
    const base = apiWorkspace ?? contextWorkspace;
    return base && nameOverride ? { ...base, name: nameOverride } : base;
  })();

  usePageTitle(displayWorkspace?.name ?? 'Workspace');

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(true); // open by default
  const handleSaved = useCallback(() => {
    setNameOverride(null);
    refetch();
  }, [refetch]);

  const isAdmin =
    currentUser?.role === 'SYSTEM_ADMIN' ||
    currentUser?.role === 'ORG_OWNER' ||
    displayWorkspace?.owner_user_id === currentUser?.id;

  const handleAsk = () =>
    navigate(`/tools/on-the-record?workspace=${id}`, {
      state: { preselectedWorkspaceId: id },
    });

  const handleBrowse = () =>
    navigate(`/tools/document-qa?workspace=${id}`, {
      state: { preselectedWorkspaceId: id },
    });

  const handleSearch = () =>
    navigate(`/tools/full-text-search?workspace=${id}`, {
      state: { preselectedWorkspaceId: id },
    });

  // ── Loading: block the page ONLY until we have a workspace row to render.
  // The workspace list typically resolves in <1s; the slow /detail payload
  // (corpus/quick stats on big scopes) fills the cards in via their own
  // skeletons, so the header/CTAs are interactive almost immediately.
  if (!displayWorkspace && (loading || workspacesLoading)) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error && !displayWorkspace) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <p className="text-sm text-red-600">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/workspaces')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Workspaces
        </Button>
      </div>
    );
  }

  if (!displayWorkspace) return null;

  const jurisdictionNames = (data?.jurisdictions ?? []).map(
    (j: { id: string; name: string }) => j.name
  );
  const tags = buildTags(displayWorkspace, jurisdictionNames);

  return (
    <div className="space-y-6 pb-16">
      {/* ── Back nav ─────────────────────────────────────────────────────── */}
      <button
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
        onClick={() => navigate('/workspaces')}
      >
        <ArrowLeft className="h-4 w-4" />
        All Workspaces
      </button>

      {/* ── Header block ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-[28px] font-bold leading-tight text-gray-900">
            {displayWorkspace.name}
          </h1>
          {displayWorkspace.description && (
            <p className="mt-1 text-sm text-gray-500">
              {displayWorkspace.description}
            </p>
          )}
          {/* Tag pills */}
          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(jurisdictionNames as string[]).map((name: string) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200
                             bg-white px-3 py-1 text-xs font-medium text-gray-600"
                >
                  {/* Map pin */}
                  <svg
                    className="h-3 w-3 text-gray-400"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 0C5.24 0 3 2.24 3 5c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 6.75A1.75 1.75 0 118 3.25 1.75 1.75 0 018 6.75z" />
                  </svg>
                  {name}
                </span>
              ))}
              {tags
                .filter((t) => !jurisdictionNames.includes(t))
                .map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full border border-gray-200
                               bg-white px-3 py-1 text-xs font-medium text-gray-600"
                  >
                    {tag.match(/^\d{4}/) ? (
                      <svg
                        className="mr-1 h-3 w-3 text-gray-400"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      >
                        <rect x="2" y="3" width="12" height="11" rx="1.5" />
                        <path d="M5 1v4M11 1v4M2 7h12" />
                      </svg>
                    ) : null}
                    {tag}
                  </span>
                ))}

              {/* ── Warnings pill — always visible when warnings exist ── */}
              {(data?.warnings ?? []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setWarningsOpen((v) => !v)}
                  className={[
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1',
                    'text-xs font-medium transition-colors',
                    warningsOpen
                      ? 'border-amber-400 bg-amber-100 text-amber-800'
                      : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
                  ].join(' ')}
                >
                  <span>⚠️</span>
                  <span>
                    {(data?.warnings ?? []).length}{' '}
                    {(data?.warnings ?? []).length !== 1
                      ? 'warnings'
                      : 'warning'}
                  </span>
                  {warningsOpen ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>
              )}
            </div>
          )}

          {/* ── Warnings panel — collapses under the tag row ── */}
          {(data?.warnings ?? []).length > 0 && warningsOpen && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 leading-relaxed">
              {(data?.warnings ?? []).map((w, i) => (
                <p key={i} className="flex items-start gap-1.5">
                  <span className="mt-px flex-shrink-0">•</span>
                  <span>{w}</span>
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Settings button */}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="flex-shrink-0 gap-1.5"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings className="h-3.5 w-3.5" />
            Settings
          </Button>
        )}
      </div>

      {/* ── CTA row ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {' '}
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleAsk}
        >
          <MessageSquare className="h-4 w-4" />
          Ask a question
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleBrowse}
        >
          <FileText className="h-4 w-4" />
          Browse documents
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleSearch}
        >
          <TextSearch className="h-4 w-4" />
          Search documents
        </Button>
      </div>

      {/* ── Two-column body ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        {/* Left column */}
        <div className="space-y-4">
          <WorkspaceCoverageCard
            stats={
              data?.corpus_stats ?? {
                total_docs: 0,
                embedded_docs: 0,
                jurisdiction_stats: [],
                governing_bodies: [],
              }
            }
            jurisdictions={data?.jurisdictions ?? []}
            loading={loading}
          />
          <WorkspaceRecentQuestions
            questions={data?.recent_questions ?? []}
            loading={loading}
            workspaceId={id ?? ''}
          />
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <WorkspaceQuickStats
            stats={
              data?.quick_stats ?? {
                total_docs: 0,
                dated_docs: 0,
                jurisdiction_count: 0,
                coverage_from: null,
                coverage_to: null,
                last_ingested_at: null,
                created_by: null,
              }
            }
            loading={loading}
          />
          <WorkspaceSuggestedQuestions
            questions={data?.suggested_questions ?? []}
            workspaceId={id ?? ''}
          />
        </div>
      </div>

      {/* ── Settings slide-out ────────────────────────────────────────────── */}
      {isAdmin && (
        <WorkspaceSettingsPanel
          workspace={displayWorkspace}
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSaved={handleSaved}
          jurisdictionNames={jurisdictionNames}
          jurisdictionIds={
            displayWorkspace?.jurisdiction_ids ??
            (displayWorkspace?.jurisdiction_id
              ? [displayWorkspace.jurisdiction_id]
              : [])
          }
        />
      )}
    </div>
  );
};
